import * as SVG from 'svg.js';

import { ColorPaletteService
       } from 'app/controls/color-chooser/color-palette.service';
import { TransformationConstraints
       } from 'app/controls/transform-handle/transform-handle';
import { GalleryService } from 'app/gallery/gallery.service';
import { TextConstants } from 'app/text/text.constants';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { FontInjectorService } from 'collage-designer/font-injector.service';
import { GlobalConstants, StageConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { TextApplianceResult, TextController
       } from '../../../text/text.controller';
import { TextControllerMethods } from '../../../text/text.controller.methods';
import { ZoomModeFit, ZoomModeFitExpand
       } from '../../../text/text.controller.zoom-mode-fit';
import { ZoomModeLimit } from '../../../text/text.controller.zoom-mode-limit';
import { plaintextToChunks, scaleModel, Text, TextChunk, withoutFontDescription
       } from '../../../text/text.model';
import { applyModel, extractChunksFromNode, extractModel
       } from '../../../text/text.view.svg';
import { CopyPlaceConfig } from '../../collage-designer-config';
import { CopyRules } from '../../copy-rules';
import { DesignStepService } from '../../design-step/design-step.service';
import { ActionBarDirective } from './overlays/action-bar/action-bar.directive';
import { Placeholder, PlaceholderType, TransformationError
       } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';

class ZoomModeError extends TrivialError
{
  // i18n.error.zoomModeError.title
  // i18n.error.zoomModeError.message
  protected _name: string = 'ZoomModeError';
}

interface TextInformation {
  id: string;
  text: Array<TextChunk>;
  isUnchanged: boolean;
}

export class TextPlaceholder extends Placeholder
{
  private currentFontSizeTranslationFactor: number;
  private minFontSize: number;
  private rect: SVG.Rect;
  private readonly eventListeners: Object = {
    'click': this.clickEvent.bind(this),
    'mouseover': this.mouseOverText.bind(this),
    'mouseleave': this.mouseLeave.bind(this)
  };

  constructor(
      errorService: ErrorService,
      loggingService: LoggingService,
      designStepService: DesignStepService,
      galleryService: GalleryService,
      private fontInjectorService: FontInjectorService,
      private colorPaletteService: ColorPaletteService,
      toolboxService: ToolboxService,
      placeholderContentService: PlaceholderContentService,
      placeholderService: PlaceholderService,
      actionBarContainer: ActionBarDirective,
      text: SVG.Text,
      partId: string)
  {
    super(
        errorService, loggingService, designStepService, galleryService,
        toolboxService, placeholderContentService, placeholderService,
        actionBarContainer, text, partId, PlaceholderType.Text, false);
    this.rect = this.wrapper.select('rect').first() as SVG.Rect;
  }

  public async setup(clickAfterModelSet: boolean): Promise<void>
  {
    if (this.isContentChangeable()) {
      this.applyChangeableEventListeners();
    }

    this.set(StageConstants.ATTRIBUTE_PENDING, true);

    this.currentFontSizeTranslationFactor = this.fontSizeTranslationFactor;

    const attributeMinFontSize =
        Number(this.get(StageConstants.ATTRIBUTE_MIN_FONT_SIZE)) || 0;

    this.minFontSize = attributeMinFontSize === 0
        ? Math.round(this.toTemplateFontSize(TextConstants.MIN_FONT_SIZE))
        : attributeMinFontSize;
    this.set(StageConstants.ATTRIBUTE_MIN_FONT_SIZE, this.minFontSize);

    this.rect.node.style.fill = 'transparent';
    this.rect.fill('transparent');

    this.element.node.style['user-select'] = 'none';

    this.initTextModel();
    const model = await this.getTextModel();

    await this.setTextModel(model);
    this.colorPaletteService.addColors([model.color]);
    this.set(StageConstants.ATTRIBUTE_PENDING, undefined);

    if (this.isLinked()) {
      await this.createLinkedText();
    }

    await this.initZoomMode();
    if (clickAfterModelSet) {
      await this.clickEvent();
    }
  }

  public async destructor(): Promise<void>
  {
    await super.destructor();
    if (this.isContentChangeable()) {
      this.removeChangeableEventListeners();
    }
  }

  public async getTextModel(): Promise<Text>
  {
    if (this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) === undefined) {
      this.initTextModel();
    }

    const model = JSON.parse(this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
    model.fontDescription =
        await this.fontInjectorService.getFontDescription(model);
    return scaleModel(model, this.currentFontSizeTranslationFactor);
  }

  private initTextModel(): void
  {
    let placeholderTextModel = {};
    if (this.get(StageConstants.ATTRIBUTE_TEXT_MODEL) === undefined) {
      const placeholderText = this.getPlaceholderText();
      placeholderTextModel = (
        (placeholderText &&
         [GlobalConstants.MODE_GOD, GlobalConstants.MODE_TRANSLATOR]
            .indexOf(Globals.mode) === -1 &&
         this.isUnchanged())
          ? {'text': placeholderText}
          : {});
    }
    else {
      placeholderTextModel = JSON.parse(
          this.get(StageConstants.ATTRIBUTE_TEXT_MODEL));
    }

    this.set(StageConstants.ATTRIBUTE_TEXT_MODEL, JSON.stringify(
        {...withoutFontDescription(extractModel(this.wrapper)),
         ...this.defaultTextModel,
         ...placeholderTextModel,
    }));
  }

  private getPlaceholderText(): Array<TextChunk> | undefined
  {
    const plaintext = this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_TEXT);

    // XML Attributes can't contain newlines. So we treat a literal '\n' as a
    // newline
    return (
      plaintext === undefined
        ? undefined
        : plaintextToChunks(plaintext.replace(/\\n/g, '\n')));
  }

  public async setTextModel(model: Text): Promise<void>
  {
    const scaledModel = scaleModel(
      model, 1 / this.currentFontSizeTranslationFactor);

    this.set(
      StageConstants.ATTRIBUTE_TEXT_MODEL,
      JSON.stringify(withoutFontDescription(scaledModel)));

    await this.displayModel(scaledModel);
  }

  private get defaultTextModel(): Text
  {
    return JSON.parse(
      this.get(StageConstants.ATTRIBUTE_DEFAULT_TEXT_MODEL) || '{}');
  }

  private async displayModel(model: Text): Promise<void>
  {
    await this.fontInjectorService.waitForCompleteFontList();

    try {
      await this.fontInjectorService.addModelFont(model, 60);
    } catch (e) {
      this.fontInjectorService.buildFontLoadingError(e, model.fontName, true);
    }

    applyModel(
      this.wrapper,
      model,
      (await this.fontInjectorService.getFontDescription(model)));
  }

  protected showEditMarker(): void
  {
    super.showEditMarker();
    this.editMarker.addClass(StageConstants.CLASS_NAME_EDIT_MARKER_STROKE);
  }

  public highlight(): void
  {
    this.showHighlightMarker();
  }

  public suspendPointerEvents(): void
  {
    this.wrapper.node.style['pointer-events'] = 'none';
  }

  public resumePointerEvents(): void
  {
    this.wrapper.node.style['pointer-events'] = 'auto';
  }

  public isFlexible(): boolean
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    return this.designStepService
      .getDesignStepConfig().textPlaceholdersSettings.showCreateNewButton;
  }

  public hasEditableContent(): boolean
  {
    return true;
  }

  private get fontSizeTranslationFactor(): number
  {
    const part = this.designStepService.getPart();
    const template = this.designStepService.getTemplate();
    const docBox = StageUtils.originalDimensions(this.element.doc());

    const rotated = template.stageConfig.rotation !== 0;

    const templateUnitsHeight = docBox.height;
    const measuredHeight = rotated ? part.measuredWidth : part.measuredHeight;

    const millimetersPerTemplateUnit = measuredHeight / templateUnitsHeight;
    const inchesPerMillimeter = 1 / 25.4;
    const pointsPerInch = 72;

    const pointsPerTemplateUnit = millimetersPerTemplateUnit *
        inchesPerMillimeter *
        pointsPerInch;

    return pointsPerTemplateUnit;
  }

  public toDisplayFontSize(templateFontSize: number): number
  {
    return templateFontSize * this.currentFontSizeTranslationFactor;
  }

  public toTemplateFontSize(displayFontSize: number): number
  {
    return displayFontSize / this.currentFontSizeTranslationFactor;
  }

  public getMinFontSize(): number
  {
    return this.minFontSize;
  }

  private applyChangeableEventListeners(): void
  {
    const text = this.element.node;

    const observer = new MutationObserver(mutations => {
      this.changed.emit(this.element.id());
    });
    observer.observe(text, {attributes: true});

    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        text.addEventListener(listener, this.eventListeners[listener]);
        this.rect.node.addEventListener(
            listener, this.eventListeners[listener]);
      }
    }
  }

  private removeChangeableEventListeners(): void
  {
    const text = this.element.node;

    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        text.removeEventListener(listener, this.eventListeners[listener]);
        this.rect.node.removeEventListener(
            listener, this.eventListeners[listener]);
      }
    }
  }

  private async mouseOverText(event: Event): Promise<void>
  {
    if (!Utils.isSmallDevice()) {
      await super.mouseOver();
      if (this.galleryService.getDraggingImage() === undefined &&
          !this.placeholderService.hasTransformHandle()) {
        if (this.isFlexible()) {
          this.wrapper.node.style.cursor = 'pointer';
        }
        else {
          this.wrapper.node.style.cursor = 'text';
        }
      }
    }
  }

  private async createLinkedText(): Promise<void>
  {
    this.detachContent();

    const currentPartId = this.designStepService.getPartId();
    const copyFrom = this.get(StageConstants.ATTRIBUTE_COPY_FROM);

    if (! copyFrom) {
      return;
    }

    let link = undefined;

    try {
      link = CopyRules.decodePlace(copyFrom, currentPartId);
    }
    catch (e) {
      this.errorService.add(e, 'PlaceholderText');
      this.errorService.flush('PlaceholderText');
    }

    if (link.part === currentPartId) {
      const superiorText = SVG.get(link.item) as SVG.Text;
      if (! superiorText) {
        return;
      }
      const observer = new MutationObserver(async mutations => {
        await this.copyTextFrom(superiorText);
      });
      observer.observe(superiorText.node, {
          attributes: true,
          attributeOldValue: true
      });

      await this.copyTextFrom(superiorText);
    }
    else {
      await this.adoptItemFromAnotherPart(link);
    }
  }

  private async adoptItemFromAnotherPart(link: CopyPlaceConfig): Promise<void>
  {
    const structure =
        await this.designStepService.getSVGStructureByPartId(link.part);
    if (!structure) {
      return;
    }
    const doc = (new DOMParser()).parseFromString(structure, 'image/svg+xml');

    const superiorText = SVG.adopt(doc.getElementById(link.item)) as SVG.Text;
    if (!superiorText) {
      return;
    }

    await this.copyTextFrom(superiorText);
  }

  private async copyTextFrom(superiorText: SVG.Text): Promise<void>
  {
    await this.setTextValidated(extractChunksFromNode(superiorText));
  }

  public get fontChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_FONT_CHANGEABLE) !== 'false';
  }

  public get fontSizeChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_FONT_SIZE_CHANGEABLE) !== 'false';
  }

  public get colorChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_COLOR_CHANGEABLE) !== 'false';
  }

  public get alignmentChangeable(): boolean
  {
    return Globals.mode === GlobalConstants.MODE_GOD ||
        this.get(StageConstants.ATTRIBUTE_ALIGNMENT_CHANGEABLE) !== 'false';
  }

  public async extractInformation(): Promise<TextInformation>
  {
    return {
      id: this.get('id'),
      text: (await this.getTextModel()).text,
      isUnchanged: this.isUnchanged(),
    };
  }

  public async applyInformation(info: TextInformation): Promise<void>
  {
    await this.setTextValidated(info.text);
  }

  public get zoomModeTextController(): TextController | undefined
  {
    switch (this.zoomMode) {
      case 'fit':
        return this.fontSizeChangeable ? ZoomModeFit : ZoomModeFitExpand;
      case 'limit':
        return ZoomModeLimit;
      default:
        return undefined;
    }
  }

  private async initZoomMode(): Promise<void>
  {
    await this.setFontValidated();
    await this.setTextValidated();
  }

  private async setFontValidated(): Promise<void>
  {
    const model = await this.getTextModel();
    const validated = this.zoomModeTextController.setFont(
        model,
        model.fontDescription,
        { minFontSize: this.toDisplayFontSize(this.getMinFontSize()) });
    if (validated.type !== TextApplianceResult.Success) {
      if (this.isLinked()) {
        this.loggingService.log(
            'TextPlaceholder',
            Severity.WARNING,
            'Possible broken template ' +
            '"' + this.designStepService.getTemplateId() + '" detected. ' +
            'Linked placeholder "' + this.wrapper.attr('id') + '" can\'t ' +
            'apply font family.');
      }
      else {
        this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
        this.errorService.flush('TextPlaceholder');
      }
      return;
    }

    this.setTextModel(validated.model);
  }

  public async setTextValidated(newText?: Array<TextChunk>): Promise<void>
  {
    const zoomMode = this.zoomModeTextController;

    if (!zoomMode) {
      this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
      this.errorService.flush('TextPlaceholder');
      return;
    }

    const model = await this.getTextModel();
    const validated = zoomMode.setText(
      model,
      newText || model.text,
      { minFontSize: this.toDisplayFontSize(this.getMinFontSize()) }
    );

    if (validated.type !== TextApplianceResult.Success) {
      if (this.isLinked()) {
        this.loggingService.log(
            'TextPlaceholder',
            Severity.WARNING,
            'Possible broken template ' +
            '"' + this.designStepService.getTemplateId() + '" detected. ' +
            'Linked placeholder "' + this.wrapper.attr('id') + '" is ' +
            'too small.');
      }
      else {
        this.errorService.add(new ZoomModeError(), 'TextPlaceholder');
        this.errorService.flush('TextPlaceholder');
      }
      return;
    }

    this.setTextModel(validated.model);
  }

  protected async getMinSize(): Promise<TransformationConstraints>
  {
    const sizes = TextControllerMethods.getTextUsageArea(
        await this.getTextModel());
    return {
      minWidth: sizes.width / this.currentFontSizeTranslationFactor,
      minHeight: sizes.height / this.currentFontSizeTranslationFactor
    };
  }

  protected async afterTransformationDone(): Promise<void>
  {
    await super.afterTransformationDone();
    const zoomMode = this.zoomModeTextController;
    const result = zoomMode.setAvailableArea(await this.getTextModel(), {
      width: this.wrapper.viewbox().width *
          this.currentFontSizeTranslationFactor,
      height: this.wrapper.viewbox().height *
          this.currentFontSizeTranslationFactor
    });
    if (result.type === TextApplianceResult.Success) {
      this.setTextModel(result.model);
    }
    else {
      this.errorService.add(new TransformationError(), 'TextPlaceholder');
      this.errorService.flush('TextPlaceholder');
    }
  }

  public doubleClick(event?: Event): void
  {
    super.doubleClick(event);
    this.hideEditMarker();
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/placeholder/text-placeholder.ts